import type { Branded } from '../brand'

export type Ulid = Branded<string, 'Ulid'>

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const TIME_CHARS = 10
const RANDOM_CHARS = 16
const RANDOM_BYTES = 10
const MAX_TIME = 2 ** 48 - 1

export class UlidError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UlidError'
  }
}

/**
 * Build a ulid from explicit parts. Core is pure: the timestamp and the
 * randomness are supplied by the caller (@gridfit/edge in production, fixtures
 * in tests), so ulids are reproducible wherever it matters.
 */
export function ulidFromParts(timestampMs: number, randomness: Uint8Array): Ulid {
  if (!Number.isSafeInteger(timestampMs) || timestampMs < 0 || timestampMs > MAX_TIME) {
    throw new UlidError(`timestamp must be an integer in [0, ${MAX_TIME}] (received ${timestampMs})`)
  }
  if (randomness.length !== RANDOM_BYTES) {
    throw new UlidError(`randomness must be ${RANDOM_BYTES} bytes (received ${randomness.length})`)
  }
  return (encodeTime(timestampMs) + encodeRandom(randomness)) as Ulid
}

export function isUlid(value: string): boolean {
  if (value.length !== TIME_CHARS + RANDOM_CHARS) return false
  for (const char of value) {
    if (!ALPHABET.includes(char)) return false
  }
  return true
}

export function parseUlid(value: string): { timestampMs: number; randomness: Uint8Array } {
  if (!isUlid(value)) throw new UlidError(`not a ulid: ${value}`)

  let timestampMs = 0
  for (let i = 0; i < TIME_CHARS; i++) {
    timestampMs = timestampMs * 32 + ALPHABET.indexOf(value[i] as string)
  }

  let bits = 0n
  for (let i = TIME_CHARS; i < value.length; i++) {
    bits = (bits << 5n) | BigInt(ALPHABET.indexOf(value[i] as string))
  }
  const randomness = new Uint8Array(RANDOM_BYTES)
  for (let i = RANDOM_BYTES - 1; i >= 0; i--) {
    randomness[i] = Number(bits & 0xffn)
    bits >>= 8n
  }

  return { timestampMs, randomness }
}

function encodeTime(timestampMs: number): string {
  let remaining = timestampMs
  let out = ''
  for (let i = 0; i < TIME_CHARS; i++) {
    out = (ALPHABET[remaining % 32] as string) + out
    remaining = Math.floor(remaining / 32)
  }
  return out
}

function encodeRandom(randomness: Uint8Array): string {
  // 10 bytes = 80 bits = exactly 16 base32 characters.
  let bits = 0n
  for (const byte of randomness) {
    bits = (bits << 8n) | BigInt(byte)
  }
  let out = ''
  for (let i = 0; i < RANDOM_CHARS; i++) {
    out = (ALPHABET[Number(bits & 31n)] as string) + out
    bits >>= 5n
  }
  return out
}
